import { useState, useRef, type ComponentRef } from 'react'
import {
  View,
  Text,
  Platform,
  useWindowDimensions,
  ScrollView,
  type LayoutChangeEvent,
} from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { AuthGradientButton } from '../../components/auth/AuthGradientButton'
import { AuthScaffold } from '../../components/auth/AuthScaffold'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import Animated, { FadeIn, FadeInDown, FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { Mail, Lock } from '@/components/ui/icons'
import { Pressable } from '../../components/ui/Pressable'
import { EmailInput, PasswordInput } from '../../components/ui/StructuredTextInput'
import { BackButton } from '../../components/ui/BackButton'
import { ConsentForm } from '../../components/auth/ConsentForm'
import { PasswordStrengthBar } from '../../components/auth/PasswordStrengthBar'
import { AuthErrorBanner } from '../../components/auth/AuthErrorBanner'
import { SocialAuthRow } from '../../components/auth/SocialAuthRow'
import { useAuth } from '../../lib/hooks/use-auth'
import { useAuthStore } from '../../lib/stores/auth.store'
import { authService } from '../../lib/services/auth.service'
import { getContactVerificationRequirement } from '../../lib/utils/account-contact'
import {
  isDuplicateAccountRegistrationError,
  isGoogleEmailExistsError,
} from '../../lib/errors/service-error'
import { registerSchema } from '../../lib/validations/auth.schema'
import type { ConsentValues } from '../../lib/types/auth.types'
import { FONTS, SPACING } from '../../lib/brand'
import { useSemanticColors, DURATION, STAGGER, INTERACTION } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { toast } from '../../lib/toast'
import { captureError, errorMessage } from '../../lib/utils/observability'

/**
 * Background retry for the post-signup `completeSignup` RPC. Used when
 * Step 2 of registration races with the session token's propagation to
 * PostgREST and fails transiently — the auth user is already created so
 * we don't want a user-facing error, just a quiet retry. Exponential
 * backoff (300ms, 1.2s, 4.8s) covers any realistic transient window;
 * if all attempts fail we capture to Sentry once at the end.
 *
 * Fire-and-forget — the caller does `void retryCompleteSignup(userId)`
 * immediately after navigating away.
 */
async function retryCompleteSignup(userId: string, attempts = 3): Promise<void> {
  for (let i = 0; i < attempts; i++) {
    const delayMs = 300 * Math.pow(4, i) // 300, 1200, 4800
    await new Promise((resolve) => setTimeout(resolve, delayMs))
    try {
      await authService.completeSignup(userId, false)
      if (__DEV__) console.log(`[Register] complete signup retry succeeded on attempt ${i + 1}`)
      return
    } catch (err) {
      if (__DEV__)
        console.warn(`[Register] complete signup retry ${i + 1}/${attempts} failed:`, err)
      if (i === attempts - 1) {
        // Final attempt failed — alert engineers via Sentry. The user is
        // already in the app and unaware; we'll surface the missing
        // consent records via a future settings-screen banner.
        captureError(
          err,
          { screen: 'register', step: 'complete_signup_retry_exhausted' },
          { userId, attempts },
        )
      }
    }
  }
}

/**
 * Visual order of the form fields. Drives two things a failed submit needs:
 * which message the banner above the CTA shows, and which field we scroll
 * to. Zod hands back issues in schema order, which is not necessarily what
 * the user is looking at.
 */
const FIELD_ORDER = ['email', 'password', 'acceptTerms'] as const

export default function RegisterScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { height } = useWindowDimensions()
  const {
    signUp,
    signInWithGoogle,
    signInWithApple,
    signUpPending,
    signUpError,
    googlePending,
    applePending,
  } = useAuth()
  const { isDesktop } = useBreakpoint()
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)
  const setPendingAuthPhone = useAuthStore((s) => s.setPendingAuthPhone)

  const reducedMotion = useReducedMotion()
  const isWide = isDesktop
  const isCompact = !isWide && height < 700
  // Same leading-icon grey the login form uses. textMuted vs textSecondary is
  // a 6% difference nobody chose — the two forms sit one tap apart.
  const iconColor = colors.textSecondary

  // Zero-friction signup: email + password + terms only.
  // Name / DOB / phone are progressive (booking + verification hub).
  const [form, setForm] = useState({
    email: '',
    password: '',
  })
  const [consent, setConsent] = useState<ConsentValues>({
    acceptTerms: false,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [postSignupError, setPostSignupError] = useState(false)

  const updateForm = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
    setPostSignupError(false)
  }

  const handleConsentChange = (values: ConsentValues) => {
    setConsent(values)
    setErrors((prev) => ({ ...prev, acceptTerms: '' }))
  }

  // Scroll handle + per-field offsets so a failed submit can move the user to
  // the field that is actually wrong. Offsets are captured from each field
  // wrapper's onLayout; they are measured against the scroll content, which is
  // exactly what `scrollTo` expects.
  const scrollRef = useRef<ComponentRef<typeof ScrollView> | null>(null)
  const fieldOffsets = useRef<Record<string, number>>({})
  const rememberOffset =
    (...keys: string[]) =>
    (event: LayoutChangeEvent) => {
      const { y } = event.nativeEvent.layout
      for (const key of keys) fieldOffsets.current[key] = y
    }

  const scrollToField = (key: string) => {
    const y = fieldOffsets.current[key]
    if (typeof y !== 'number') return
    scrollRef.current?.scrollTo({ y: Math.max(y - SPACING.xl, 0), animated: true })
  }

  // Synchronous double-submit guard — `signUpPending` (TanStack state) lags by
  // a render, so a rapid second tap can create two auth users before disabled
  // state kicks in. Same pattern as app/booking/checkout.tsx.
  const isRegisteringRef = useRef(false)
  // Remember whether step 1 succeeded so that retrying only retries step 2.
  const createdUserIdRef = useRef<string | null>(null)

  const handleRegister = async () => {
    if (isRegisteringRef.current) return
    impact(ImpactStyle.Light)
    setPostSignupError(false)

    const input = {
      email: form.email,
      password: form.password,
      acceptTerms: consent.acceptTerms as true,
      preferredLocale: (i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'es') as 'en' | 'es',
    }

    const result = registerSchema.safeParse(input)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const key = issue.path[0] as string
        fieldErrors[key] = t(issue.message)
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      // Haptics are a no-op on web and the per-field messages can sit far
      // above the CTA, so the banner right above the button (wired to
      // `firstFieldError`) is the feedback that always lands. Move the user
      // to the offending field as well.
      const firstKey = FIELD_ORDER.find((key) => fieldErrors[key])
      if (firstKey) scrollToField(firstKey)
      return
    }
    setErrors({})

    isRegisteringRef.current = true
    // Lock AuthGate redirect until full signup flow completes
    setCompletingSignup(true)

    // Step 1: Create the Supabase Auth user — skip if a previous attempt
    // already succeeded here and only step 2 failed.
    let userId = createdUserIdRef.current
    if (!userId) {
      try {
        const signUpResult = await signUp(result.data)
        userId = signUpResult.userId
        createdUserIdRef.current = userId
      } catch (err) {
        if (__DEV__) console.error('[Register] sign up failed:', err)
        setCompletingSignup(false)
        void notification(NotificationType.Error)
        const message = errorMessage(err, t('auth.errors.signup_failed'))
        // A duplicate email already gets two pieces of feedback: the field
        // error under the offending input and the banner above the CTA (via
        // `firstFieldError`). A third simultaneous toast is noise.
        if (isDuplicateAccountRegistrationError(err)) {
          setErrors((prev) => ({ ...prev, email: message }))
        } else {
          toast.error(message)
        }
        captureError(err, { screen: 'register', step: 'signup' })
        isRegisteringRef.current = false
        return
      }
    }

    // Step 2: Atomic legal + consent + audit via SECURITY DEFINER RPC.
    // Works even without an active session (email confirmation pending).
    //
    // FAIL-SOFT on this step: the auth user is already created (Step 1
    // succeeded). A failure here is a transient race (most commonly the
    // session token hasn't fully propagated to PostgREST yet, < 100ms
    // window) and the consent records can be written on the next session
    // start without the user noticing. Carlos's "could not create
    // account" toast was firing here even though the account WAS created
    // — misleading UX. We now: (a) log the error for engineers (DevTools
    // + Sentry), (b) schedule a fire-and-forget background retry that
    // re-attempts up to 3 times with exponential backoff, (c) navigate
    // forward unconditionally because the user is created and signed in.
    try {
      await authService.completeSignup(userId, false)
    } catch (err) {
      if (__DEV__) console.error('[Register] complete signup failed:', err)
      captureError(err, { screen: 'register', step: 'complete_signup' }, { userId })
      void retryCompleteSignup(userId)
      // Intentionally NO toast.error / NO setPostSignupError — account
      // is created, session is live, user proceeds. If all retries fail,
      // Sentry alerts engineers and a future settings-screen banner can
      // surface the missing consent record.
    }

    // Both steps done — clear the ref so future sessions start fresh.
    createdUserIdRef.current = null
    void notification(NotificationType.Success)

    // Same entry rule as Google/Apple: one verified contact is enough.
    // Email signup verifies the EMAIL. Phone is optional
    // metadata and can be linked later in verification — never a peaje
    // between "Crear cuenta" and the app. AuthGate also uses this helper.
    useAuthStore.getState().markCredentialUnlock()
    // Never stage a forced phone handoff after email signup. Optional phone
    // already lives in auth user_metadata for later linking.
    setPendingAuthPhone(null)
    try {
      const session = await authService.getSession()
      const requirement = getContactVerificationRequirement(session?.user ?? null, null)
      const route =
        requirement === 'email'
          ? '/(auth)/verify-email'
          : requirement === 'phone'
            ? '/(auth)/verify-phone'
            : '/(tabs)/explore'
      router.replace(route as never)
    } catch (err) {
      captureError(err, { screen: 'register', step: 'post_signup_route' }, { userId })
      // Safe default for email/password: finish email verification, not SMS.
      router.replace('/(auth)/verify-email' as never)
    }
    isRegisteringRef.current = false
  }

  const handleSocialRegister = async (
    provider: () => Promise<unknown>,
    providerName: 'google' | 'apple',
  ) => {
    // Social sign-up implies Terms + Privacy acceptance via the inline
    // caption rendered below the social-auth row (same pattern the
    // welcome screen uses). The form's Terms checkbox is only required
    // for the email/password path because that flow has no consent
    // moment otherwise. This matches Airbnb / Stripe / Wise — clicking
    // "Sign up with Google" is itself the consent gesture.
    setCompletingSignup(true)
    // Web OAuth is a full-page redirect — stash intent so RootLayout's PKCE
    // exchange can call completeSignup after return.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.sessionStorage.setItem('tago.oauth.register_intent', '1')
    }
    try {
      await provider()

      // Social auth resolves in-app on native. On web, provider() returns after
      // starting the redirect and there is no session yet — RootLayout finishes.
      const session = await authService.getSession()
      if (!session?.user.id) {
        if (Platform.OS === 'web') return // redirect in flight; keep completing flag
        setCompletingSignup(false)
        return
      }
      try {
        await authService.completeSignup(session.user.id, false)
      } catch (err) {
        captureError(
          err,
          { screen: 'register', step: `${providerName}_complete_signup` },
          { userId: session.user.id },
        )
      }
      // Unlock sticky biometric-fail and seed handoff like login.
      useAuthStore.getState().markCredentialUnlock()
      const requirement = getContactVerificationRequirement(session.user, null)
      const route =
        requirement === 'email'
          ? '/(auth)/verify-email'
          : requirement === 'phone'
            ? '/(auth)/verify-phone'
            : '/(tabs)/explore'
      router.replace(route as never)
    } catch (err) {
      if (__DEV__) console.error(`[Register] ${providerName} registration failed:`, err)
      // Email already belongs to an account → send them to log in with that
      // method instead of a dead-end toast on the signup screen.
      if (isGoogleEmailExistsError(err)) {
        setCompletingSignup(false)
        toast.info(t('auth.errors.google_email_exists'))
        router.replace('/(auth)/login')
        return
      }
      void notification(NotificationType.Error)
      toast.error(
        errorMessage(
          err,
          t(providerName === 'google' ? 'auth.errors.google_failed' : 'auth.errors.social_failed'),
        ),
      )
      captureError(err, { screen: 'register', step: `${providerName}_oauth` })
      setCompletingSignup(false)
    }
  }

  const handleGoogleRegister = () => handleSocialRegister(() => signInWithGoogle(), 'google')
  const handleAppleRegister = () => handleSocialRegister(() => signInWithApple(), 'apple')
  const handlePhoneRegister = () =>
    router.push({ pathname: '/(auth)/phone-signup' as never, params: { mode: 'signup' } })

  const signUpErrorMessage = signUpError
    ? errorMessage(signUpError, t('auth.errors.signup_failed'))
    : null
  // First validation message in visual order. The CTA is never disabled: a
  // dead button with no explanation is worse than a live one that tells you
  // what is missing, and `registerSchema` already validates the Terms
  // checkbox (`auth.validation.accept_terms_required`). A failed submit
  // therefore always lands here — the one piece of feedback adjacent to the
  // button on every platform, including web where haptics do not exist.
  const firstFieldError = FIELD_ORDER.reduce<string | null>(
    (found, key) => found ?? (errors[key] || null),
    null,
  )
  const bannerError =
    firstFieldError ??
    (postSignupError ? t('auth.errors.consent_write_failed') : signUpErrorMessage)

  // Shared form content used by both mobile and desktop layouts
  const formContent = (
    <>
      {/* Back button */}
      <BackButton />

      {/* Headline */}
      <Animated.Text
        entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER)}
        accessibilityRole="header"
        numberOfLines={2}
        adjustsFontSizeToFit
        minimumFontScale={0.88}
        style={{
          fontFamily: FONTS.bodyBold,
          // 32/38 on a phone, like the login headline. The two screens
          // already agreed at the desktop breakpoint (28) and diverged by
          // 2pt on the one form factor everybody actually sees.
          fontSize: isWide ? 28 : isCompact ? 26 : 32,
          lineHeight: isWide ? 34 : isCompact ? 30 : 38,
          marginTop: isWide ? SPACING.lg - SPACING.xs : isCompact ? SPACING.md : SPACING.lg,
          color: colors.textPrimary,
          textAlign: isWide ? 'left' : 'center',
        }}
      >
        {t('register.headline')}
      </Animated.Text>

      {/* Subtitle */}
      <Animated.Text
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)
        }
        style={{
          fontFamily: FONTS.body,
          fontSize: 16,
          lineHeight: 24,
          marginTop: SPACING.sm,
          marginBottom: isWide ? SPACING.lg : SPACING.md,
          // Was textPrimary — two 16pt lines as dark as the headline right
          // above them flattened the whole header block.
          color: colors.textSecondary,
          textAlign: isWide ? 'left' : 'center',
        }}
      >
        {t('register.subtitle')}
      </Animated.Text>

      {/* Social sign-up — primary path. Industry pattern (Airbnb / Stripe /
          Wise / Linear): social-auth options at the top, then a divider,
          then the email/password form. Clicking Google is itself the Terms
          + Privacy acceptance — captured by the inline caption below. */}
      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 3)
        }
      >
        <SocialAuthRow
          onGoogle={handleGoogleRegister}
          onApple={handleAppleRegister}
          onPhone={handlePhoneRegister}
          googleLoading={googlePending}
          appleLoading={applePending}
          isDark={isDark}
          testIDPrefix="register"
        />
      </Animated.View>

      {/* Inline consent caption for the social path */}
      <Animated.View
        collapsable={false}
        entering={reducedMotion ? undefined : FadeIn.duration(DURATION.normal).delay(STAGGER * 4)}
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'center',
          // The links below are 44pt boxes; without an explicit cross-axis
          // rule the plain runs of text stretch to that height and stop
          // sharing a baseline with them.
          alignItems: 'center',
          rowGap: SPACING.xs,
          // The 44pt boxes supply the air that `SPACING.sm + 2` used to —
          // and 10 was not a step on the spacing scale.
          marginTop: SPACING.xs,
        }}
      >
        <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
          {t('welcome.legal_prefix')}
        </Text>
        {/* Identical anatomy to the welcome screen's legal row: 13pt, link in
            textPrimary against secondary prose, and a real target — RNW drops
            hitSlop, so minHeight + centred content is what makes the box
            tappable. */}
        <Pressable
          onPress={() => router.push('/legal/terms-of-service' as never)}
          accessibilityRole="link"
          accessibilityLabel={t('welcome.legal_terms')}
          hitSlop={8}
          testID="register.legal-terms"
          style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 13,
              textDecorationLine: 'underline',
              color: colors.textPrimary,
            }}
          >
            {t('welcome.legal_terms')}
          </Text>
        </Pressable>
        <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
          {t('welcome.legal_and')}
        </Text>
        <Pressable
          onPress={() => router.push('/legal/privacy-policy' as never)}
          accessibilityRole="link"
          accessibilityLabel={t('welcome.legal_privacy')}
          hitSlop={8}
          testID="register.legal-privacy"
          style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 13,
              textDecorationLine: 'underline',
              color: colors.textPrimary,
            }}
          >
            {t('welcome.legal_privacy')}
          </Text>
        </Pressable>
      </Animated.View>

      {/* Divider — separates the social path above from the email/password
          form below. Same treatment as the welcome screen. */}
      <Animated.View
        collapsable={false}
        entering={reducedMotion ? undefined : FadeIn.duration(DURATION.normal).delay(STAGGER * 4)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: SPACING.lg,
          marginBottom: SPACING.lg,
        }}
      >
        <View style={{ flex: 1, height: 1, backgroundColor: colors.border }} />
        <Text
          style={{
            marginHorizontal: SPACING.md,
            fontFamily: FONTS.body,
            // 13, like the connector on welcome and login. Three screens in
            // one flow were rendering this same hairline-plus-label at three
            // different sizes.
            fontSize: 13,
            letterSpacing: 0.2,
            color: colors.textSecondary,
          }}
        >
          {t('register.email_divider')}
        </Text>
        <View style={{ flex: 1, height: 1, backgroundColor: colors.border }} />
      </Animated.View>

      {/* Email */}
      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)
        }
        onLayout={rememberOffset('email')}
      >
        <EmailInput
          label={t('auth.email')}
          placeholder={t('auth.email_placeholder')}
          value={form.email}
          onChangeText={(v) => updateForm('email', v)}
          error={errors.email}
          leftIcon={<Mail size={18} color={iconColor} />}
          testID="register.email-input"
        />
      </Animated.View>

      {/* Password — single field; strength bar shows rules (no confirm). */}
      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 5)
        }
        onLayout={rememberOffset('password')}
      >
        <PasswordInput
          label={t('auth.password')}
          placeholder="••••••••"
          value={form.password}
          onChangeText={(v) => updateForm('password', v)}
          autoComplete="new-password"
          textContentType="newPassword"
          error={errors.password}
          leftIcon={<Lock size={18} color={iconColor} />}
          testID="register.password-input"
        />
      </Animated.View>

      <PasswordStrengthBar
        password={form.password}
        isDark={isDark}
        testID="register.password-strength"
      />

      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 18,
          color: colors.textMuted,
          marginBottom: SPACING.md,
        }}
        testID="register.progressive-hint"
      >
        {t('auth.register_progressive_hint')}
      </Text>

      {/* Consent */}
      <View onLayout={rememberOffset('acceptTerms')}>
        <ConsentForm values={consent} onChange={handleConsentChange} testIDPrefix="register" />
      </View>

      {/* Error banner — the one piece of feedback that is always adjacent to
          the CTA, on every platform. */}
      <AuthErrorBanner error={bannerError ?? null} marginTop />

      {/* CTA button — gradient */}
      <View
        testID="register.submit-anchor"
        style={{
          // The wide and default branches were the same value written twice.
          marginTop: isCompact ? SPACING.lg - SPACING.xs : SPACING.xl - SPACING.xs,
        }}
      >
        <AuthGradientButton
          title={t('auth.create_account_button')}
          onPress={handleRegister}
          loading={signUpPending}
          testID="register.submit-button"
          entering={
            reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 9)
          }
        />
      </View>

      {/* Login link */}
      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 10)
        }
        className="flex-row justify-center"
        style={{
          alignItems: 'center',
          // Same non-wrapping-row overflow the other three auth footers had:
          // at 2x text the prompt plus the link is wider than the screen and
          // the glyphs spill off both edges. `flexWrap` is on the style object
          // rather than the className because the NativeWind class only sets
          // direction and justification.
          flexWrap: 'wrap',
          rowGap: SPACING.xs,
          marginTop: isWide ? SPACING.lg : isCompact ? SPACING.md : SPACING.lg,
          marginBottom: SPACING.sm,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 14,
            flexShrink: 1,
            color: colors.textSecondary,
          }}
        >
          {`${t('auth.already_have_account')} `}
        </Text>
        <Pressable
          onPress={() => router.replace('/(auth)/login')}
          hitSlop={8}
          accessibilityRole="link"
          accessibilityLabel={t('auth.login')}
          testID="register.login-link"
          style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              // The mirror of this link on the login screen is textAccent.
              // This one was brand purple in light and coral in dark — the
              // same footer link painted three different colours across two
              // screens and two themes.
              color: colors.textAccent,
            }}
          >
            {t('auth.login')}
          </Text>
        </Pressable>
      </Animated.View>
    </>
  )

  return (
    <AuthScaffold
      brandHeadline={t('welcome.headline')}
      brandSubtitle={t('welcome.subheadline')}
      background={colors.background}
      columnWidth="formWide"
      scrollRef={scrollRef}
    >
      {formContent}
    </AuthScaffold>
  )
}
